import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  disconnectOutlook,
  formatApiError,
  getOutlookConnectUrl,
  getOutlookStatus,
  importOutlookEvents,
  previewOutlookWeek,
} from '../../services/api';

function formatEventWhen(ev, allDayLabel) {
  if (!ev) return '';
  const day = ev.day || '';
  const endDay = ev.end_day || day;
  const start = (ev.start || '').slice(0, 5);
  const end = (ev.end || '').slice(0, 5);
  if (ev.is_all_day) {
    return endDay !== day ? `${day} → ${endDay} ${allDayLabel}` : `${day} ${allDayLabel}`;
  }
  if (endDay !== day) {
    return `${day} ${start} → ${endDay} ${end}`;
  }
  return `${day} ${start}–${end}`;
}

export default function ImportOutlookModal({
  isOpen,
  onClose,
  weekStartIso,
  returnToPath,
  onImported,
}) {
  const { t } = useTranslation(['planning', 'common']);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [statusReason, setStatusReason] = useState(null);
  const [authorizeUrl, setAuthorizeUrl] = useState(null);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [resultSummary, setResultSummary] = useState(null);

  const selectableIds = useMemo(
    () => events.filter((e) => !e.already_imported).map((e) => e.outlook_id),
    [events]
  );

  const selectedCount = useMemo(
    () => [...selected].filter((id) => selectableIds.includes(id)).length,
    [selected, selectableIds]
  );

  const loadPreview = useCallback(async () => {
    if (!weekStartIso) return;
    setLoading(true);
    setError(null);
    setResultSummary(null);
    try {
      const status = await getOutlookStatus(
        returnToPath || '/employee-dashboard/planning/week'
      );
      setConfigured(status?.configured !== false);
      const isConnected = Boolean(status?.connected);
      const nextAuthorizeUrl = status?.authorize_url || null;
      setConnected(isConnected);
      setStatusReason(isConnected ? null : status?.reason || null);
      setAuthorizeUrl(isConnected ? null : nextAuthorizeUrl);
      if (!isConnected) {
        setEvents([]);
        setSelected(new Set());
        // Send the user to Microsoft login when status provides authorize_url.
        if (nextAuthorizeUrl && typeof window !== 'undefined') {
          window.location.assign(nextAuthorizeUrl);
          return;
        }
        return;
      }
      const data = await previewOutlookWeek(weekStartIso);
      const list = Array.isArray(data?.events) ? data.events : [];
      setEvents(list);
      setSelected(new Set(list.filter((e) => !e.already_imported).map((e) => e.outlook_id)));
    } catch (err) {
      const reconnect = Boolean(err?.data?.reconnect_required);
      if (reconnect) {
        setConnected(false);
        setEvents([]);
        setSelected(new Set());
      }
      const formatted = formatApiError(err);
      setError(
        formatted.generalError ||
          err?.data?.detail ||
          t('failedToLoadOutlook')
      );
    } finally {
      setLoading(false);
    }
  }, [weekStartIso, returnToPath]);

  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const importingRef = React.useRef(importing);
  importingRef.current = importing;
  const disconnectingRef = React.useRef(disconnecting);
  disconnectingRef.current = disconnecting;

  useEffect(() => {
    if (!isOpen) return undefined;
    setError(null);
    setResultSummary(null);
    loadPreview();
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !importingRef.current && !disconnectingRef.current) {
        onCloseRef.current?.();
      }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, weekStartIso, loadPreview]);

  const handleConnect = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prefer authorize_url already returned by status.
      if (authorizeUrl) {
        window.location.assign(authorizeUrl);
        return;
      }
      const data = await getOutlookConnectUrl(
        returnToPath || '/employee-dashboard/planning/week'
      );
      if (data?.authorize_url) {
        window.location.assign(data.authorize_url);
        return;
      }
      setError(t('couldNotStartMsConnection'));
    } catch (err) {
      const formatted = formatApiError(err);
      setError(formatted.generalError || err?.data?.detail || t('connectionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await disconnectOutlook();
      setConnected(false);
      setEvents([]);
      setSelected(new Set());
    } catch (err) {
      const formatted = formatApiError(err);
      setError(formatted.generalError || t('failedToDisconnectOutlook'));
    } finally {
      setDisconnecting(false);
    }
  };

  const toggleId = (id, disabled) => {
    if (disabled) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedCount === selectableIds.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };

  const handleImport = async () => {
    const ids = [...selected].filter((id) => selectableIds.includes(id));
    if (!ids.length) {
      setError(t('selectAtLeastOneEvent'));
      return;
    }
    setImporting(true);
    setError(null);
    setResultSummary(null);
    try {
      const result = await importOutlookEvents({
        week_start: weekStartIso,
        outlook_event_ids: ids,
        category: 'HO',
      });
      const created = result?.created_count ?? result?.created?.length ?? 0;
      const skipped = result?.skipped_count ?? result?.skipped?.length ?? 0;
      setResultSummary({ created, skipped });
      await onImported?.(result);
      await loadPreview();
    } catch (err) {
      const reconnect = Boolean(err?.data?.reconnect_required);
      if (reconnect) setConnected(false);
      const formatted = formatApiError(err);
      setError(
        formatted.generalError ||
          err?.data?.detail ||
          t('failedToImportOutlook')
      );
    } finally {
      setImporting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  const busy = loading || importing || disconnecting;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className="weekly-view-root fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            if (!busy) onClose?.();
          }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-outlook-title"
            className="wv-outlook-modal w-full max-w-lg bg-white rounded-xl border border-[#e5e7eb] shadow-[0_12px_40px_rgba(18,65,145,0.18)] overflow-hidden flex flex-col max-h-[min(90vh,640px)]"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#e5e7eb] shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <span className="material-symbols-outlined text-[20px]">calendar_month</span>
                </span>
                <div className="min-w-0">
                  <h2 id="import-outlook-title" className="text-sm font-semibold text-[#0f172a]">
                    Import from Outlook
                  </h2>
                  <p className="text-xs text-[#64748b] mt-0.5">
                    Choose events from this week to add to your planning. Outlook is never modified.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="text-[#94a3b8] hover:text-[#0f172a] p-1 rounded-md"
                onClick={() => !busy && onClose?.()}
              aria-label={t('common:close')}
                disabled={busy}
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
              {!configured ? (
                <p className="text-sm text-[#b45309] bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Outlook import is not configured on the server. Ask an admin to set Azure credentials.
                </p>
              ) : null}

              {error ? (
                <p className="text-sm text-[#b91c1c] bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">
                  {typeof error === 'string' ? error : t('common:somethingWentWrong')}
                </p>
              ) : null}

              {resultSummary ? (
                <p className="text-sm text-[#065f46] bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-3">
                  Imported {resultSummary.created} event
                  {resultSummary.created === 1 ? '' : 's'}
                  {resultSummary.skipped
                    ? ` · ${resultSummary.skipped} skipped (overlap or already imported)`
                    : ''}
                  . You can delete imported events from the weekly calendar.
                </p>
              ) : null}

              {loading ? (
                <div className="flex items-center gap-2 text-sm text-[#64748b] py-8 justify-center">
                  <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
                  Loading…
                </div>
              ) : !connected ? (
                <div className="py-6 text-center space-y-3">
                  <p className="text-sm text-[#475569]">
                    Sign in with your own Microsoft account to preview and import this
                    week&apos;s Outlook events. An admin account is not required.
                  </p>
                  {statusReason ? (
                    <p className="text-xs text-[#64748b] bg-[#f8fafc] border border-[#e2e8f0] rounded-lg px-3 py-2 text-left">
                      {statusReason}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:opacity-90"
                    onClick={handleConnect}
                    disabled={busy || !configured}
                  >
                    <span className="material-symbols-outlined text-[18px]">link</span>
                    Sign in with your Microsoft account
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <button
                      type="button"
                      className="text-xs font-medium text-primary hover:underline"
                      onClick={toggleAll}
                      disabled={!selectableIds.length}
                    >
                      {selectedCount === selectableIds.length && selectableIds.length
                        ? t('uncheckAll')
                        : t('checkAll')}
                    </button>
                    <button
                      type="button"
                      className="text-xs text-[#64748b] hover:text-[#b91c1c]"
                      onClick={handleDisconnect}
                      disabled={busy}
                    >
                      Disconnect
                    </button>
                  </div>

                  {!events.length ? (
                    <p className="text-sm text-[#64748b] py-6 text-center">
                      No Outlook events found for this week.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {events.map((ev) => {
                        const disabled = Boolean(ev.already_imported);
                        const checked = disabled || selected.has(ev.outlook_id);
                        return (
                          <li key={ev.outlook_id}>
                            <label
                              className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${
                                disabled
                                  ? 'border-[#e2e8f0] bg-[#f8fafc] opacity-70 cursor-default'
                                  : checked
                                    ? 'border-primary/40 bg-primary/[0.04]'
                                    : 'border-[#e5e7eb] hover:border-primary/30'
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => toggleId(ev.outlook_id, disabled)}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium text-[#0f172a] truncate">
                                  {ev.subject || '(No title)'}
                                </span>
                                <span className="block text-xs text-[#64748b] mt-0.5">
                                  {formatEventWhen(ev, t('allDayParen'))}
                                  {disabled ? ' · Already imported' : ''}
                                </span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#e5e7eb] shrink-0 bg-[#fafafa]">
              <button
                type="button"
                className="px-3 py-1.5 text-xs font-medium text-[#475569] hover:text-[#0f172a] rounded-lg"
                onClick={() => onClose?.()}
                disabled={busy}
              >
                Close
              </button>
              {connected ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:opacity-90 disabled:opacity-50"
                  onClick={handleImport}
                  disabled={busy || selectedCount === 0}
                >
                  {importing ? (
                    <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>
                  ) : (
                    <span className="material-symbols-outlined text-[16px]">download</span>
                  )}
                  Import selected ({selectedCount})
                </button>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
